<template>
  <input
    type="text"
    ref="autocompleteInput"
    :id="inputId"
    :aria-label="ariaLabel || (inputId ? null : placeholder)"
    :placeholder="placeholder"
    role="combobox"
    aria-autocomplete="list"
    aria-haspopup="listbox"
    :aria-expanded="isSuggestionsExpanded ? 'true' : 'false'"
  />
</template>

<script>
import { mapState } from 'vuex'

import googleMapsLoader, { importLibrary } from '@/mixins/googleMapsLoader'

export default {
  mixins: [googleMapsLoader],
  data() {
    return {
      isSuggestionsExpanded: false,
      autocomplete: null,
      // Guards against an earlier createAutoComplete() call resolving after a
      // later one and binding stale country restrictions.
      createRequestId: 0,
    }
  },
  props: ['placeholder', 'requiresAddress', 'countryCodes', 'inputId', 'ariaLabel'],
  computed: {
    ...mapState(['storeConfig']),
    // Match RadarAutoComplete: prop -> store_locator_country_codes -> country_code -> US
    computedCountryCodes() {
      const normalize = (codes) =>
        codes
          .map((code) => String(code).trim().toLowerCase())
          .filter(Boolean)
          // Google Places Autocomplete allows at most 5 countries
          .slice(0, 5)

      if (this.countryCodes && this.countryCodes.length > 0) {
        return normalize(this.countryCodes)
      }

      const storeLocatorCodes = this.storeConfig?.store_locator_country_codes

      if (Array.isArray(storeLocatorCodes) && storeLocatorCodes.length > 0) {
        return normalize(storeLocatorCodes)
      }

      if (typeof storeLocatorCodes === 'string' && storeLocatorCodes.trim()) {
        return normalize(storeLocatorCodes.split(/[,\s]+/))
      }

      return normalize([this.storeConfig?.country_code || 'US'])
    },
  },
  watch: {
    requiresAddress() {
      this.resetInput()
      this.createAutoComplete()
    },
    computedCountryCodes(newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.createAutoComplete()
      }
    },
  },
  methods: {
    async createAutoComplete() {
      const input = this.$refs.autocompleteInput
      if (!input) return

      const requestId = ++this.createRequestId

      let type = this.requiresAddress ? ['address'] : ['geocode']
      const options = {
        types: type,
        componentRestrictions: { country: this.computedCountryCodes },
        fields: ['address_components', 'geometry'],
      }
      const { Autocomplete } = await importLibrary('places')
      // A newer call started while the places library was loading, so this one
      // would bind outdated options over it.
      if (requestId !== this.createRequestId) return

      this.destroyAutoComplete()

      const autocomplete = new Autocomplete(input, options)
      this.autocomplete = autocomplete
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        this.isSuggestionsExpanded = false
        this.$emit('placeChanged', place)
      })
    },
    destroyAutoComplete() {
      if (!this.autocomplete) return
      // Without this the previous instance stays bound to the same input, so it
      // keeps its own suggestions dropdown and re-emits placeChanged with the
      // country restrictions it was created with.
      window.google?.maps?.event?.clearInstanceListeners(this.autocomplete)
      this.autocomplete = null
    },
    // Bound once in mounted() — createAutoComplete() can run several times and
    // these would stack up, emitting `input` once per past call per keystroke.
    bindInputListeners() {
      const input = this.$refs.autocompleteInput
      if (!input) return
      input.addEventListener('input', () => {
        this.isSuggestionsExpanded = input.value.trim().length > 0
        this.$emit('input', input.value)
      })
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' || event.key === 'Enter' || event.key === 'Tab') {
          this.isSuggestionsExpanded = false
        }
      })
      input.addEventListener('focus', () => {
        this.isSuggestionsExpanded = input.value.trim().length > 0
      })
      input.addEventListener('blur', () => {
        this.isSuggestionsExpanded = false
      })
    },
    resetInput() {
      this.$refs.autocompleteInput.value = ''
      this.isSuggestionsExpanded = false
    },
    focusInput() {
      this.$refs.autocompleteInput.focus()
    },
    addInputValue(value) {
      this.$refs.autocompleteInput.value = value
      this.isSuggestionsExpanded = value && value.trim().length > 0
    },
  },
  mounted() {
    this.bindInputListeners()
    this.createAutoComplete()
  },
  beforeUnmount() {
    this.destroyAutoComplete()
  },
}
</script>

<style scoped></style>
